import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  Chip,
  Divider,
  Paper,
  CardActionArea,
  CardContent,
  CircularProgress,
  Grid,
  LinearProgress,
  Checkbox,
  FormControlLabel,
  InputAdornment,
  MenuItem,
  Stack,
  Step,
  StepLabel,
  Stepper,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import RocketLaunchIcon from "@mui/icons-material/RocketLaunch";
import PaidIcon from "@mui/icons-material/Paid";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { useTranslation } from "react-i18next";
import { presentationUserFacingError } from "../../utils/presentationUserFacingError";
import {
  deriveSlugFromSocialProfileUrl,
  isGenericSlugFromSocialHost,
  isSocialProfileUrl,
  socialNetworkLabel,
} from "../../utils/presentationUrlValidation";

const MODE_OPTIONS = [
  {
    id: "presentation",
    Icon: AutoAwesomeIcon,
    color: "secondary",
  },
  {
    id: "trial",
    Icon: RocketLaunchIcon,
    color: "primary",
  },
  {
    id: "pay",
    Icon: PaidIcon,
    color: "success",
  },
];

function normalizeSlug(raw) {
  let s = String(raw || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "-");
  s = s.replace(/^https?:\/\//, "");
  const slash = s.indexOf("/");
  if (slash >= 0) s = s.slice(0, slash);
  return s.replace(/\.xpro\.me$/i, "").replace(/\.$/, "").trim();
}

const SLUG_RE = /^[a-z0-9][a-z0-9_-]{1,31}$/;

const CREATE_SUBSCRIPTION_STATUSES = ["trialing", "active", "onboarding", "courtesy"];

function uniquePlanOptions(items) {
  const list = Array.isArray(items) ? items : [];
  const byKey = new Map();
  const skuVersions = {};
  for (const p of list) {
    const sku = String(p?.sku || "").trim();
    if (!sku) continue;
    const ver = Number(p.version) > 0 ? Number(p.version) : 1;
    const key = `${sku}::${ver}`;
    if (byKey.has(key)) continue;
    byKey.set(key, { sku, ver, name: p.name || sku });
    skuVersions[sku] = (skuVersions[sku] || 0) + 1;
  }
  return Array.from(byKey.entries()).map(([value, p]) => ({
    value,
    label: skuVersions[p.sku] > 1 ? `${p.name} — v${p.ver}` : p.name,
  }));
}

function workspaceCreateSubmitError(e, t) {
  const code = String(e?.response?.data?.code || e?.code || "").trim();
  const cloneFailed = t("workspaceCreate.errors.cloneFailed", {
    defaultValue:
      "Não foi possível preparar o ambiente agora. O identificador continua disponível — toque em Criar workspace para tentar de novo. Se repetir, fale com o suporte.",
  });
  const byCode = {
    workspace_clone_failed: cloneFailed,
    workspace_schema_exists: t("workspaceCreate.errors.schemaExists", {
      defaultValue: "Já existe um ambiente com esse identificador. Escolha outro nome.",
    }),
    workspace_template_misconfigured: t("workspaceCreate.errors.templateMisconfigured", {
      defaultValue: "O modelo de workspace não está configurado no servidor. Fale com o suporte.",
    }),
    workspace_identifier_taken: t("workspaceCreate.errors.identifierTaken", {
      defaultValue: "Este identificador já está em uso. Escolha outro.",
    }),
    workspace_dominio_persist_failed: t("workspaceCreate.errors.dominioPersistFailed", {
      defaultValue:
        "Não foi possível registrar o workspace na plataforma. Tente de novo; se repetir, fale com o suporte.",
    }),
    plan_required_for_pay: t("workspaceCreate.errors.planRequired", {
      defaultValue: "Selecione um plano.",
    }),
    invalid_workspace_slug: t("workspaceCreate.errors.invalidSlug", {
      defaultValue: "Informe um identificador válido (2–32 caracteres: letras minúsculas, números, - ou _).",
    }),
  };
  if (byCode[code]) return byCode[code];
  const msg = typeof e?.response?.data?.message === "string" ? e.response.data.message.trim() : "";
  if (/clonar banco/i.test(msg) || /clonar o banco/i.test(msg)) return cloneFailed;
  return (
    msg ||
    e?.message ||
    t("workspaceCreate.errors.submitFailed", {
      defaultValue:
        "Não foi possível criar o workspace agora. Confira os dados e tente de novo. Se o problema continuar, entre em contato com o suporte.",
    })
  );
}

/** ID numérico do domínio após POST /dominios (camelCase ou legado). */
function createdWorkspaceDominioId(res) {
  if (!res || typeof res !== "object") return null;
  const raw = res.dominioId ?? res.dominio_id ?? res.id;
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? n : null;
}

/**
 * Deriva um identificador a partir da URL do site (apenas para sugestão automática).
 * Ex.: "https://www.piracanjuba.com.br/" → "piracanjuba"
 *      "empresa-x.com"                   → "empresa-x"
 *      "sub.dominio.empresa.com.br"      → "empresa"
 *
 * Estratégia:
 *  1) extrai o hostname (com ou sem protocolo);
 *  2) remove "www.";
 *  3) remove sufixos comuns de TLD/2LD (.com.br, .com, .net, .org, etc.) pegando o "registrable name";
 *  4) sanitiza para o regex de slug: minúsculas, [a-z0-9_-], sem hífen/underline na borda inicial.
 */
function deriveSlugFromSiteUrl(raw) {
  const v = String(raw || "").trim();
  if (!v) return "";
  let host = v;
  host = host.replace(/^https?:\/\//i, "").replace(/^\/\//, "");
  const slash = host.indexOf("/");
  if (slash >= 0) host = host.slice(0, slash);
  host = host.split("?")[0].split("#")[0].split(":")[0];
  host = host.toLowerCase().trim();
  host = host.replace(/^www\./, "");
  if (!host) return "";
  const parts = host.split(".").filter(Boolean);
  if (!parts.length) return "";
  const COMMON_2LD = new Set([
    "com",
    "co",
    "net",
    "org",
    "gov",
    "edu",
    "mil",
    "ac",
    "br",
    "uk",
    "us",
    "io",
    "app",
    "dev",
    "tech",
    "ai",
    "me",
    "tv",
    "xyz",
    "store",
    "online",
    "site",
  ]);
  let candidate = parts[parts.length - 1];
  const GENERIC_HOSTS = new Set([
    "instagram",
    "facebook",
    "www",
    "linkedin",
    "tiktok",
    "youtube",
    "twitter",
    "x",
  ]);
  if (GENERIC_HOSTS.has(candidate)) return "";
  for (let i = parts.length - 2; i >= 0; i--) {
    if (COMMON_2LD.has(parts[i])) {
      candidate = parts[i - 1] || parts[i];
    } else {
      candidate = parts[i];
      break;
    }
  }
  let slug = String(candidate || "")
    .toLowerCase()
    .replace(/[^a-z0-9_-]+/g, "-")
    .replace(/^[-_]+/, "")
    .replace(/[-_]+$/, "");
  if (slug.length > 32) slug = slug.slice(0, 32).replace(/[-_]+$/, "");
  return slug;
}

const PRESENTATION_SOURCE_TYPES = ["website", "social", "brief"];

function deriveSlugFromBrief(brief) {
  const name = String(brief?.companyName || brief?.tradeName || "").trim();
  if (!name) return "";
  let slug = name
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9_-]+/g, "-")
    .replace(/^[-_]+/, "")
    .replace(/[-_]+$/, "");
  if (slug.length > 32) slug = slug.slice(0, 32).replace(/[-_]+$/, "");
  return slug;
}

function PRESENTATION_PIPELINE_STEPS(t) {
  return [
    { key: "queued", label: t("workspaceCreate.progress.queued", { defaultValue: "Na fila…" }) },
    { key: "scraping", label: t("workspaceCreate.progress.scraping", { defaultValue: "Coletando informações do site" }) },
    { key: "generating", label: t("workspaceCreate.progress.generating", { defaultValue: "Gerando dados de demonstração" }) },
    { key: "applying", label: t("workspaceCreate.progress.applying", { defaultValue: "Aplicando no workspace" }) },
    { key: "ready", label: t("workspaceCreate.progress.ready", { defaultValue: "Pronto!" }) },
  ];
}

/** Índice do stepper para status retornado pela API (inclui estados fora da lista linear). */
function presentationStatusStepIndex(status) {
  const s = String(status || "queued").toLowerCase();
  const order = ["queued", "scraping", "generating", "applying", "ready"];
  const i = order.indexOf(s);
  if (i >= 0) return i;
  if (s === "converting") return 3;
  return 0;
}

/**
 * Componente wizard reutilizável para criação de workspaces.
 *
 * Props:
 *  - checkSlugAvailability: async (normalizedSlug) => { available, domain?, reason? } — checagem em tempo real
 *  - createWorkspace: async ({ mode, workspace, siteUrl, planSku, planVersion }) => result
 *      result deve incluir { dominioId, presentationId? } na resposta de sucesso.
 *  - getPresentationStatus: async (dominioId) => { presentation: { status, errorMessage, ... } }
 *      usado para polling no modo `presentation`.
 *  - planCatalogItems: array opcional para preencher o seletor de plano.
 *  - showClassificationFields: master — perfil de cobrança, representante, segmento e situação.
 *  - resellerItems / segmentItems: opções dos selects de classificação.
 *  - allowedModes: array opcional para restringir os modos exibidos (default: todos).
 *  - onCompleted: callback chamado com `{ dominioId, mode, ... }` ao final.
 *  - openWorkspaceUrl: opcional — string ou função (workspaceData) => string para link "abrir".
 */
const SLUG_CHECK_DEBOUNCE_MS = 450;

const WorkspaceCreateWizard = ({
  checkSlugAvailability,
  createWorkspace,
  getPresentationStatus,
  planCatalogItems,
  showClassificationFields = false,
  resellerItems,
  segmentItems,
  allowedModes,
  onCompleted,
  openWorkspaceUrl,
  defaultMode,
  hidePlanSelection = false,
  modelTemplateItems,
  showTemplatePicker = true,
}) => {
  const { t } = useTranslation("app");
  const modes = useMemo(() => {
    if (!Array.isArray(allowedModes) || !allowedModes.length) return MODE_OPTIONS;
    const set = new Set(allowedModes);
    return MODE_OPTIONS.filter((m) => set.has(m.id));
  }, [allowedModes]);

  const [activeStep, setActiveStep] = useState(0);
  const [mode, setMode] = useState(defaultMode || (modes[0] ? modes[0].id : "trial"));
  const [slug, setSlug] = useState("");
  const [slugCustomized, setSlugCustomized] = useState(false);
  const [slugAvailability, setSlugAvailability] = useState({ status: "idle", domain: "", reason: null });
  const [noWebsite, setNoWebsite] = useState(false);
  const [presentationSourceType, setPresentationSourceType] = useState("social");
  const [siteUrl, setSiteUrl] = useState("");
  const [socialUrl, setSocialUrl] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [tradeName, setTradeName] = useState("");
  const [segment, setSegment] = useState("");
  const [briefDescription, setBriefDescription] = useState("");
  const [briefCity, setBriefCity] = useState("");
  const [planSku, setPlanSku] = useState("");
  const [planVersion, setPlanVersion] = useState(1);
  const [billingProfile, setBillingProfile] = useState("commercial");
  const [resellerId, setResellerId] = useState("");
  const [workspaceSegment, setWorkspaceSegment] = useState("");
  const [subscriptionStatus, setSubscriptionStatus] = useState("");
  const [selectedTemplateId, setSelectedTemplateId] = useState("blank");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [createdResult, setCreatedResult] = useState(null);
  const [presentationStatus, setPresentationStatus] = useState(null);
  const [pollError, setPollError] = useState("");
  const [pollFailureCount, setPollFailureCount] = useState(0);
  const [manualRefreshBusy, setManualRefreshBusy] = useState(false);
  const [staleQueuedHint, setStaleQueuedHint] = useState(false);
  const pollTimerRef = useRef(null);
  const progressStartedAtRef = useRef(null);

  const stepsLabels = useMemo(() => {
    const detailsLabel =
      mode === "presentation"
        ? t("workspaceCreate.steps.company", { defaultValue: "Empresa e endereço" })
        : showClassificationFields
          ? t("workspaceCreate.steps.setup", { defaultValue: "Endereço e classificação" })
          : t("workspaceCreate.steps.details", { defaultValue: "Endereço do workspace" });
    const labels = [
      t("workspaceCreate.steps.mode", { defaultValue: "Tipo de ambiente" }),
      detailsLabel,
    ];
    if (mode === "presentation") {
      labels.push(t("workspaceCreate.steps.progress", { defaultValue: "Geração da apresentação" }));
    }
    labels.push(t("workspaceCreate.steps.done", { defaultValue: "Concluído" }));
    return labels;
  }, [mode, showClassificationFields, t]);

  const finalStep = stepsLabels.length - 1;
  const presentationProgressStep = mode === "presentation" ? finalStep - 1 : null;

  const onPresentationProgress =
    presentationProgressStep != null && activeStep === presentationProgressStep;
  const showWizardFooter =
    activeStep !== finalStep && !onPresentationProgress;
  const showBackInFooter = activeStep >= 1 && showWizardFooter;

  const stopPolling = useCallback(() => {
    if (pollTimerRef.current) {
      clearTimeout(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  }, []);

  useEffect(() => () => stopPolling(), [stopPolling]);

  const siteUrlLooksSocial = useMemo(
    () => !noWebsite && isSocialProfileUrl(siteUrl),
    [noWebsite, siteUrl]
  );

  const siteUrlFieldError = useMemo(() => {
    if (!siteUrl.trim() || noWebsite) return "";
    if (siteUrlLooksSocial) {
      const network = socialNetworkLabel(siteUrl);
      return t("workspaceCreate.errors.siteUrlIsSocial", {
        network,
        defaultValue: `Este link é do ${network || "uma rede social"}, não do site. Use a opção abaixo.`,
      });
    }
    return "";
  }, [siteUrl, noWebsite, siteUrlLooksSocial, t]);

  const slugError = useMemo(() => {
    if (!slug) return "";
    const norm = normalizeSlug(slug);
    if (!SLUG_RE.test(norm)) {
      return t("workspaceCreate.errors.invalidSlug", {
        defaultValue: "2–32 caracteres: letras minúsculas, números, - ou _",
      });
    }
    if (isGenericSlugFromSocialHost(norm)) {
      return t("workspaceCreate.errors.slugFromSocialHost", {
        defaultValue: "Escolha um identificador da empresa, não o nome da rede social.",
      });
    }
    return "";
  }, [slug, t]);

  const normalizedSlug = useMemo(() => normalizeSlug(slug), [slug]);

  const slugDomainSuffix = useMemo(() => {
    const d = String(slugAvailability.domain || "").trim();
    if (d.includes(".")) return d.slice(d.indexOf("."));
    return ".xpro.me";
  }, [slugAvailability.domain]);

  const stepLead = useMemo(() => {
    if (activeStep === 0) {
      return showClassificationFields
        ? t("workspaceCreate.leads.modePlatform", {
            defaultValue:
              "Como este ambiente vai ser usado. No próximo passo: identificador, classificação (uso interno, representante, segmento) e plano, se houver cobrança.",
          })
        : t("workspaceCreate.leads.mode", {
            defaultValue: "Como este ambiente vai ser usado. No próximo passo você define o identificador.",
          });
    }
    if (activeStep === 1) {
      if (mode === "presentation") {
        return showClassificationFields
          ? t("workspaceCreate.leads.detailsPresentationPlatform", {
              defaultValue:
                "Fonte da empresa, identificador e classificação. A geração da demo é o passo seguinte.",
            })
          : t("workspaceCreate.leads.detailsPresentation", {
              defaultValue: "Informe o site da empresa (ou outra fonte) e o identificador do workspace.",
            });
      }
      if (mode === "pay") {
        return showClassificationFields
          ? t("workspaceCreate.leads.detailsPayPlatform", {
              defaultValue:
                "Identificador, classificação e plano. Uso interno ou desenvolvimento dispensam o plano.",
            })
          : t("workspaceCreate.leads.detailsPay", {
              defaultValue: "Defina o identificador e o plano de cobrança.",
            });
      }
      return showClassificationFields
        ? t("workspaceCreate.leads.detailsTrialPlatform", {
            defaultValue: "Identificador e classificação. O plano de trial é opcional se a cobrança for comercial.",
          })
        : t("workspaceCreate.leads.detailsTrial", {
            defaultValue: "Defina o identificador. O plano de trial é opcional.",
          });
    }
    return "";
  }, [activeStep, mode, showClassificationFields, t]);

  const planOptions = useMemo(() => uniquePlanOptions(planCatalogItems), [planCatalogItems]);

  const isNonCommercialBilling =
    showClassificationFields && (billingProfile === "internal" || billingProfile === "development");

  const showPlanField =
    !hidePlanSelection && (mode === "trial" || mode === "pay") && !isNonCommercialBilling;

  const templateOptions = useMemo(() => {
    const list = Array.isArray(modelTemplateItems) ? modelTemplateItems : [];
    const published = list.filter((x) => String(x?.status || "published") === "published");
    if (!workspaceSegment) return published;
    return published.filter(
      (x) => String(x.segmentKey || "").toLowerCase() === String(workspaceSegment).toLowerCase()
    );
  }, [modelTemplateItems, workspaceSegment]);

  const showTemplates =
    showTemplatePicker && mode !== "presentation" && (mode === "trial" || mode === "pay");

  const resellerOptions = useMemo(
    () => (Array.isArray(resellerItems) ? resellerItems.filter((r) => r?.id) : []),
    [resellerItems]
  );

  const segmentOptions = useMemo(
    () => (Array.isArray(segmentItems) ? segmentItems.filter((s) => s?.segmentKey) : []),
    [segmentItems]
  );

  const billingProfileLabel = useCallback(
    (profile) =>
      t(`platformPage.billingProfile.${String(profile || "").toLowerCase()}`, {
        defaultValue: profile,
      }),
    [t]
  );

  const subscriptionStatusLabel = useCallback(
    (status) =>
      t(`billingPage.subscriptionStatus.${String(status || "").toLowerCase()}`, {
        defaultValue: status,
      }),
    [t]
  );

  const planSelectValue = planSku ? `${planSku}::${Number(planVersion) || 1}` : "";

  const slugCanCheckAvailability = Boolean(
    checkSlugAvailability && normalizedSlug && SLUG_RE.test(normalizedSlug) && !slugError
  );

  useEffect(() => {
    if (!checkSlugAvailability) {
      setSlugAvailability({ status: "idle", domain: "", reason: null });
      return undefined;
    }
    if (!slugCanCheckAvailability) {
      setSlugAvailability({ status: "idle", domain: "", reason: null });
      return undefined;
    }
    let cancelled = false;
    const timer = setTimeout(async () => {
      setSlugAvailability({ status: "checking", domain: "", reason: null });
      try {
        const res = await checkSlugAvailability(normalizedSlug);
        if (cancelled) return;
        if (res?.available) {
          setSlugAvailability({
            status: "available",
            domain: res.domain || `${normalizedSlug}.xpro.me`,
            reason: res?.orphanSchema || res?.reason === "orphan_schema" ? "orphan_schema" : null,
          });
          return;
        }
        if (res?.reason === "reserved") {
          setSlugAvailability({
            status: "reserved",
            domain: res.domain || `${normalizedSlug}.xpro.me`,
            reason: "reserved",
          });
          return;
        }
        setSlugAvailability({
          status: "taken",
          domain: res.domain || `${normalizedSlug}.xpro.me`,
          reason: res?.reason || "taken",
        });
      } catch {
        if (!cancelled) {
          setSlugAvailability({ status: "error", domain: "", reason: "error" });
        }
      }
    }, SLUG_CHECK_DEBOUNCE_MS);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [checkSlugAvailability, normalizedSlug, slugCanCheckAvailability]);

  const slugAvailabilityMessage = useMemo(() => {
    if (slugError) return slugError;
    if (!normalizedSlug) {
      return t("workspaceCreate.fields.slugHelp", {
        defaultValue: "Minúsculas, números, hífen ou _. Vira o endereço deste workspace.",
      });
    }
    if (slugAvailability.status === "checking") {
      return t("workspaceCreate.fields.slugChecking", { defaultValue: "Verificando disponibilidade…" });
    }
    if (slugAvailability.status === "available") {
      if (slugAvailability.reason === "orphan_schema") {
        return t("workspaceCreate.fields.slugOrphan", {
          domain: slugAvailability.domain || `${normalizedSlug}.xpro.me`,
          defaultValue:
            "Livre para reutilizar ({{domain}}). Uma tentativa anterior ficou pela metade — criar de novo limpa e segue.",
        });
      }
      return t("workspaceCreate.fields.slugAvailable", {
        domain: slugAvailability.domain || `${normalizedSlug}.xpro.me`,
        defaultValue: "Este endereço está livre: {{domain}}",
      });
    }
    if (slugAvailability.status === "reserved") {
      return t("workspaceCreate.fields.slugReserved", {
        defaultValue: "Nome reservado pelo sistema. Escolha outro.",
      });
    }
    if (slugAvailability.status === "taken") {
      return t("workspaceCreate.fields.slugTaken", {
        defaultValue: "Este identificador já está em uso. Escolha outro.",
      });
    }
    if (slugAvailability.status === "error") {
      return t("workspaceCreate.fields.slugCheckError", {
        defaultValue: "Não foi possível verificar agora. Tente de novo em instantes.",
      });
    }
    return t("workspaceCreate.fields.slugHelp", {
      defaultValue: "Minúsculas, números, hífen ou _. Vira o endereço deste workspace.",
    });
  }, [slugError, normalizedSlug, slugAvailability, t]);

  const slugFieldHasError =
    Boolean(slugError) || slugAvailability.status === "taken" || slugAvailability.status === "reserved";

  const slugFieldSuccess = slugAvailability.status === "available" && !slugError;

  const slugSubmitBlocked =
    Boolean(slugError) ||
    slugAvailability.status === "checking" ||
    slugAvailability.status === "taken" ||
    slugAvailability.status === "reserved" ||
    (checkSlugAvailability && slugCanCheckAvailability && slugAvailability.status !== "available");

  const moveSiteUrlToSocial = useCallback(() => {
    const url = siteUrl.trim();
    if (!url) return;
    setNoWebsite(true);
    setPresentationSourceType("social");
    setSocialUrl(url);
    setSiteUrl("");
    setError("");
    if (!slugCustomized || isGenericSlugFromSocialHost(normalizeSlug(slug))) {
      const fromSocial = deriveSlugFromSocialProfileUrl(url);
      if (fromSocial) setSlug(fromSocial);
      else if (isGenericSlugFromSocialHost(normalizeSlug(slug))) setSlug("");
    }
  }, [siteUrl, slug, slugCustomized]);

  const startPresentationPolling = useCallback(
    (dominioId) => {
      if (!dominioId || !getPresentationStatus) return;
      progressStartedAtRef.current = Date.now();
      setPollError("");
      setPollFailureCount(0);
      setStaleQueuedHint(false);
      const fetchOnce = async () => {
        try {
          const res = await getPresentationStatus(dominioId);
          setPollError("");
          setPollFailureCount(0);
          const polledId = createdWorkspaceDominioId(res);
          if (polledId) {
            setCreatedResult((prev) => {
              if (prev && createdWorkspaceDominioId(prev)) return prev;
              return { ...(prev && typeof prev === "object" ? prev : {}), dominioId: polledId };
            });
          }
          const pres = res?.presentation ?? null;
          setPresentationStatus(pres);
          const status = pres?.status;
          const elapsed = progressStartedAtRef.current ? Date.now() - progressStartedAtRef.current : 0;
          const low = String(status || "").toLowerCase();
          const serverErr =
            (typeof pres?.errorMessage === "string" && pres.errorMessage.trim()) ||
            (typeof pres?.error_message === "string" && pres.error_message.trim()) ||
            "";
          if (low === "failed" || serverErr) {
            setStaleQueuedHint(false);
          } else if (low === "queued" && elapsed > 120_000) {
            setStaleQueuedHint(true);
          } else {
            setStaleQueuedHint(false);
          }
          if (status === "ready" || status === "failed" || status === "discarded") {
            stopPolling();
            if (status === "ready") {
              setActiveStep(finalStep);
            }
            return;
          }
        } catch (e) {
          const msg =
            (typeof e?.response?.data?.message === "string" && e.response.data.message) ||
            e?.message ||
            "";
          setPollError(
            msg ||
              t("workspaceCreate.progress.pollErrorGeneric", {
                defaultValue:
                  "Não foi possível atualizar o status agora. Verifique sua internet, atualize a página ou entre na conta novamente. Se repetir, espere alguns minutos ou entre em contato com o suporte.",
              })
          );
          setPollFailureCount((c) => c + 1);
        }
        pollTimerRef.current = setTimeout(fetchOnce, 4000);
      };
      stopPolling();
      pollTimerRef.current = setTimeout(fetchOnce, 1500);
    },
    [getPresentationStatus, stopPolling, finalStep, t]
  );

  const refreshPresentationStatus = useCallback(async () => {
    const id = createdWorkspaceDominioId(createdResult);
    if (!id || !getPresentationStatus) return;
    setManualRefreshBusy(true);
    setPollError("");
    try {
      const res = await getPresentationStatus(id);
      setPollFailureCount(0);
      const pres = res?.presentation ?? null;
      setPresentationStatus(pres);
      const status = pres?.status;
      if (status === "ready" || status === "failed" || status === "discarded") {
        stopPolling();
        if (status === "ready") setActiveStep(finalStep);
      }
    } catch (e) {
      const msg =
        (typeof e?.response?.data?.message === "string" && e.response.data.message) ||
        e?.message ||
        t("workspaceCreate.progress.pollErrorGeneric", {
          defaultValue:
            "Não foi possível atualizar o status agora. Verifique sua internet, atualize a página ou entre na conta novamente. Se repetir, espere alguns minutos ou entre em contato com o suporte.",
        });
      setPollError(msg);
    } finally {
      setManualRefreshBusy(false);
    }
  }, [createdResult, getPresentationStatus, stopPolling, finalStep, t]);

  const handleSubmit = useCallback(async () => {
    setError("");
    const norm = normalizeSlug(slug);
    if (!SLUG_RE.test(norm)) {
      setError(
        t("workspaceCreate.errors.invalidSlug", {
          defaultValue: "Informe um identificador válido (2–32 caracteres).",
        })
      );
      return;
    }
    if (mode === "presentation" && isGenericSlugFromSocialHost(norm)) {
      setError(
        t("workspaceCreate.errors.slugFromSocialHost", {
          defaultValue: "Escolha um identificador da empresa, não o nome da rede social.",
        })
      );
      return;
    }
    if (checkSlugAvailability && slugCanCheckAvailability && slugAvailability.status !== "available") {
      setError(
        slugAvailability.status === "taken"
          ? t("workspaceCreate.fields.slugTaken", {
              defaultValue: "Este identificador já está em uso. Escolha outro.",
            })
          : slugAvailability.status === "reserved"
            ? t("workspaceCreate.fields.slugReserved", {
                defaultValue: "Nome reservado pelo sistema. Escolha outro.",
              })
            : t("workspaceCreate.errors.slugNotAvailable", {
                defaultValue: "Aguarde a verificação do identificador ou escolha outro.",
              })
      );
      return;
    }
    if (mode === "presentation") {
      if (!noWebsite) {
        if (!siteUrl.trim()) {
          setError(t("workspaceCreate.errors.siteRequired", { defaultValue: "Informe o site da empresa." }));
          return;
        }
        if (isSocialProfileUrl(siteUrl)) {
          setError(
            t("workspaceCreate.errors.siteUrlIsSocial", {
              network: socialNetworkLabel(siteUrl),
              defaultValue: `Este link é de ${socialNetworkLabel(siteUrl) || "rede social"}. Marque que o cliente não tem site e use Rede social.`,
            })
          );
          return;
        }
      } else if (presentationSourceType === "social") {
        if (!socialUrl.trim()) {
          setError(
            t("workspaceCreate.errors.socialRequired", {
              defaultValue: "Informe o link do perfil (Instagram, Facebook, LinkedIn, etc.).",
            })
          );
          return;
        }
      } else if (presentationSourceType === "brief") {
        const hasName = companyName.trim().length >= 2;
        const hasDesc = briefDescription.trim().length >= 20;
        if (!hasName && !hasDesc) {
          setError(
            t("workspaceCreate.errors.briefRequired", {
              defaultValue: "Informe o nome da empresa ou uma descrição com pelo menos 20 caracteres.",
            })
          );
          return;
        }
      }
    }
    if (showPlanField && mode === "pay" && !planSku) {
      setError(t("workspaceCreate.errors.planRequired", { defaultValue: "Selecione um plano." }));
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        mode,
        workspace: norm,
      };
      if (mode === "presentation") {
        if (!noWebsite) {
          payload.presentationSourceType = "website";
          payload.siteUrl = siteUrl.trim();
        } else if (presentationSourceType === "social") {
          payload.presentationSourceType = "social";
          payload.socialUrl = socialUrl.trim();
        } else {
          payload.presentationSourceType = "brief";
          payload.companyBrief = {
            companyName: companyName.trim(),
            tradeName: tradeName.trim(),
            segment: segment.trim(),
            description: briefDescription.trim(),
            city: briefCity.trim(),
          };
        }
      }
      if (showPlanField && (mode === "trial" || mode === "pay") && planSku) {
        payload.planSku = planSku;
        payload.planVersion = Number(planVersion) || 1;
      }
      if (showClassificationFields) {
        payload.billingProfile = billingProfile || "commercial";
        if (resellerId) payload.resellerId = Number(resellerId);
        if (workspaceSegment) payload.workspaceSegment = workspaceSegment;
        if (!isNonCommercialBilling && (mode === "trial" || mode === "pay") && subscriptionStatus) {
          payload.subscriptionStatus = subscriptionStatus;
        }
      }
      if (showTemplates && selectedTemplateId && selectedTemplateId !== "blank") {
        payload.modelTemplateId = Number(selectedTemplateId);
      }
      const res = await createWorkspace(payload);
      const dominioId = createdWorkspaceDominioId(res);
      const resNorm = res && typeof res === "object" ? { ...res, dominioId } : res;
      setCreatedResult(resNorm);
      if (mode === "presentation") {
        if (!dominioId) {
          setError(
            t("workspaceCreate.errors.missingDominioId", {
              defaultValue:
                "Não foi possível confirmar a criação do workspace. Atualize a página e tente novamente, ou fale com o suporte.",
            })
          );
          return;
        }
        setActiveStep(presentationProgressStep);
        startPresentationPolling(dominioId);
      } else {
        setActiveStep(finalStep);
      }
      if (typeof onCompleted === "function" && mode !== "presentation") {
        onCompleted({ ...(resNorm || {}), mode });
      }
    } catch (e) {
      const code = String(e?.response?.data?.code || e?.code || "");
      const rawMsg = String(e?.response?.data?.message || "");
      const looksStuck =
        code === "workspace_identifier_taken" ||
        code === "workspace_schema_exists" ||
        /identificador indisponível/i.test(rawMsg);
      if (looksStuck && checkSlugAvailability && normalizedSlug) {
        try {
          const res = await checkSlugAvailability(normalizedSlug);
          if (res?.available) {
            setSlugAvailability({
              status: "available",
              domain: res.domain || `${normalizedSlug}.xpro.me`,
              reason: "orphan_schema",
            });
            setError(
              t("workspaceCreate.errors.leftoverRetry", {
                defaultValue:
                  "Uma tentativa anterior deixou este identificador preso. Ele continua livre — toque em Criar workspace de novo para reaproveitá-lo.",
              })
            );
            return;
          }
          setSlugAvailability({
            status: "taken",
            domain: res?.domain || `${normalizedSlug}.xpro.me`,
            reason: "taken",
          });
        } catch {
          /* keep generic mapping below */
        }
      }
      setError(workspaceCreateSubmitError(e, t));
    } finally {
      setSubmitting(false);
    }
  }, [
    slug,
    mode,
    noWebsite,
    presentationSourceType,
    siteUrl,
    socialUrl,
    companyName,
    tradeName,
    segment,
    briefDescription,
    briefCity,
    planSku,
    planVersion,
    billingProfile,
    resellerId,
    workspaceSegment,
    subscriptionStatus,
    showClassificationFields,
    showPlanField,
    isNonCommercialBilling,
    createWorkspace,
    presentationProgressStep,
    finalStep,
    onCompleted,
    startPresentationPolling,
    checkSlugAvailability,
    slugCanCheckAvailability,
    slugAvailability,
    t,
  ]);

  const handleNext = () => {
    if (activeStep === 1) {
      handleSubmit();
      return;
    }
    setActiveStep((s) => Math.min(finalStep, s + 1));
  };

  const handleBack = () => {
    setActiveStep((s) => Math.max(0, s - 1));
  };

  const renderModeStep = () => (
    <Grid container spacing={2}>
      {modes.map((opt) => {
        const Icon = opt.Icon;
        const selected = mode === opt.id;
        return (
          <Grid item xs={12} md={4} key={opt.id}>
            <Card
              variant={selected ? "elevation" : "outlined"}
              elevation={selected ? 3 : 0}
              sx={(theme) => {
                const c = theme.palette[opt.color];
                return {
                  height: "100%",
                  borderColor: selected ? c.main : theme.palette.divider,
                  borderWidth: selected ? 2 : 1,
                  bgcolor: selected ? alpha(c.main, 0.08) : theme.palette.background.paper,
                  transition: theme.transitions.create(["box-shadow", "border-color", "background-color"], {
                    duration: theme.transitions.duration.shorter,
                  }),
                  "&:hover": {
                    borderColor: selected ? c.main : alpha(theme.palette.text.primary, 0.28),
                  },
                };
              }}
            >
              <CardActionArea onClick={() => setMode(opt.id)} sx={{ height: "100%", alignItems: "stretch" }}>
                <CardContent>
                  <Stack direction="row" alignItems="flex-start" spacing={1.5} sx={{ mb: 1.25 }}>
                    <Avatar
                      variant="rounded"
                      sx={(theme) => ({
                        bgcolor: alpha(theme.palette[opt.color].main, selected ? 0.22 : 0.12),
                        width: 44,
                        height: 44,
                      })}
                    >
                      <Icon fontSize="small" color={opt.color} />
                    </Avatar>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="subtitle1" component="h3" fontWeight={700}>
                        {t(`workspaceCreate.mode.${opt.id}.title`, {
                          defaultValue:
                            opt.id === "presentation"
                              ? "Apresentação"
                              : opt.id === "trial"
                                ? "Trial"
                                : "Pagamento",
                        })}
                      </Typography>
                      {selected ? (
                        <Typography variant="caption" color="primary" fontWeight={600} sx={{ display: "block", mt: 0.25 }}>
                          {t("workspaceCreate.modeSelectedPill", { defaultValue: "Selecionado" })}
                        </Typography>
                      ) : null}
                    </Box>
                    {selected ? <CheckCircleIcon color={opt.color} sx={{ fontSize: 22, mt: 0.25 }} /> : null}
                  </Stack>
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.55 }}>
                    {t(
                      showClassificationFields && opt.id === "pay"
                        ? "workspaceCreate.mode.pay.descriptionPlatform"
                        : `workspaceCreate.mode.${opt.id}.description`,
                      {
                        defaultValue:
                          opt.id === "presentation"
                            ? "Demo para reunião comercial, com dados fictícios a partir do site (ou outra fonte)."
                            : opt.id === "trial"
                              ? "Ambiente real no período de avaliação, sem cobrança neste fluxo."
                              : showClassificationFields
                                ? "Ambiente real com plano do catálogo. Uso interno você marca na classificação, no próximo passo."
                                : "Ambiente real com plano do catálogo.",
                      }
                    )}
                  </Typography>
                </CardContent>
              </CardActionArea>
            </Card>
          </Grid>
        );
      })}
    </Grid>
  );

  const renderDetailsStep = () => {
    const slugField = (
      <TextField
        label={t("workspaceCreate.fields.slug", { defaultValue: "Identificador" })}
        helperText={slugAvailabilityMessage}
        error={slugFieldHasError}
        color={slugFieldSuccess ? "success" : undefined}
        value={slug}
        onChange={(e) => {
          setSlug(e.target.value);
          setSlugCustomized(true);
        }}
        size="small"
        autoFocus={mode !== "presentation"}
        fullWidth
        inputProps={{
          spellCheck: false,
          autoCapitalize: "none",
          autoCorrect: "off",
          autoComplete: "off",
        }}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end" sx={{ maxWidth: "50%" }}>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mr: 0.75, whiteSpace: "nowrap", userSelect: "none" }}
                aria-hidden
              >
                {slugDomainSuffix}
              </Typography>
              {slugAvailability.status === "checking" ? (
                <CircularProgress size={18} aria-label={t("workspaceCreate.fields.slugChecking")} />
              ) : slugFieldSuccess ? (
                <CheckCircleIcon color="success" fontSize="small" aria-hidden />
              ) : slugFieldHasError && normalizedSlug ? (
                <ErrorOutlineIcon color="error" fontSize="small" aria-hidden />
              ) : null}
            </InputAdornment>
          ),
        }}
      />
    );

    return (
      <Stack spacing={2}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          spacing={1}
          sx={(theme) => ({
            px: 1.75,
            py: 1.25,
            borderRadius: 2,
            border: `1px solid ${theme.palette.divider}`,
            bgcolor: alpha(theme.palette.primary.main, 0.04),
          })}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
              {t("workspaceCreate.modeSummary.label", { defaultValue: "Tipo selecionado" })}
            </Typography>
            <Typography variant="body2" fontWeight={700} noWrap>
              {t(`workspaceCreate.mode.${mode}.title`, {
                defaultValue:
                  mode === "presentation" ? "Apresentação" : mode === "trial" ? "Trial" : "Pagamento",
              })}
            </Typography>
          </Box>
          <Button
            size="small"
            variant="text"
            onClick={() => {
              setError("");
              setActiveStep(0);
            }}
            sx={{ flexShrink: 0, textTransform: "none", fontWeight: 600 }}
          >
            {t("workspaceCreate.actions.changeType", { defaultValue: "Alterar" })}
          </Button>
        </Stack>

        {mode === "presentation" ? (
          <>
            <Alert severity="success" variant="outlined" sx={{ py: 1 }}>
              <Typography variant="body2" component="div">
                {t("workspaceCreate.sourceType.website.priority", {
                  defaultValue:
                    "O site da empresa é a melhor fonte: a demonstração fica mais fiel, com produtos, serviços e tom de voz corretos.",
                })}
              </Typography>
            </Alert>

            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
              <Stack spacing={1.5}>
                <Box>
                  <Typography variant="subtitle2" fontWeight={700}>
                    {t("workspaceCreate.sections.websiteTitle", { defaultValue: "Site da empresa" })}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                    {t("workspaceCreate.sections.websiteLead", {
                      defaultValue:
                        "Endereço do site institucional ou loja virtual — não use Instagram, Facebook nem outras redes aqui.",
                    })}
                  </Typography>
                </Box>

                {!noWebsite ? (
                  <TextField
                    label={t("workspaceCreate.fields.siteUrl", { defaultValue: "URL do site" })}
                    helperText={
                      siteUrlFieldError ||
                      t("workspaceCreate.fields.siteUrlHelp", {
                        defaultValue: "Ex.: https://www.suaempresa.com.br",
                      })
                    }
                    error={Boolean(siteUrlFieldError)}
                    value={siteUrl}
                    onChange={(e) => {
                      const value = e.target.value;
                      setSiteUrl(value);
                      setError("");
                      if (!slugCustomized && value && !isSocialProfileUrl(value)) {
                        const auto = deriveSlugFromSiteUrl(value);
                        if (auto) setSlug(auto);
                      }
                    }}
                    placeholder="https://www.empresa.com.br"
                    size="small"
                    autoFocus
                    fullWidth
                  />
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    {t("workspaceCreate.sections.websiteSkipped", {
                      defaultValue: "Você indicou que o cliente não tem site. Preencha os dados na seção abaixo.",
                    })}
                  </Typography>
                )}

                {siteUrlLooksSocial ? (
                  <Alert
                    severity="warning"
                    variant="outlined"
                    action={
                      <Button color="warning" size="small" variant="outlined" onClick={moveSiteUrlToSocial}>
                        {t("workspaceCreate.actions.useAsSocial", { defaultValue: "Usar como rede social" })}
                      </Button>
                    }
                  >
                    {t("workspaceCreate.alerts.socialInSiteField", {
                      network: socialNetworkLabel(siteUrl),
                      defaultValue: `Este endereço é do ${socialNetworkLabel(siteUrl) || "uma rede social"}, não do site da empresa. Marque "O cliente não tem site" e informe o link em Rede social — ou informe o site .com/.com.br do cliente.`,
                    })}
                  </Alert>
                ) : null}

                <Divider />

                <FormControlLabel
                  sx={{ alignItems: "flex-start", m: 0 }}
                  control={
                    <Checkbox
                      size="small"
                      checked={noWebsite}
                      sx={{ pt: 0.25 }}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        setNoWebsite(checked);
                        setError("");
                        if (checked) setSiteUrl("");
                      }}
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body2" fontWeight={600}>
                        {t("workspaceCreate.fields.noWebsite", {
                          defaultValue: "O cliente não tem site",
                        })}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block">
                        {t("workspaceCreate.fields.noWebsiteHelp", {
                          defaultValue: "Marque só se realmente não existir site. Aí você pode usar perfil em rede social ou um brief.",
                        })}
                      </Typography>
                    </Box>
                  }
                />
              </Stack>
            </Paper>

            {noWebsite ? (
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                <Stack spacing={1.5}>
                  <Typography variant="subtitle2" fontWeight={700}>
                    {t("workspaceCreate.sections.fallbackTitle", {
                      defaultValue: "Sem site — outra fonte",
                    })}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t("workspaceCreate.fields.sourceFallbackLabel", {
                      defaultValue: "Escolha como a IA vai conhecer a empresa:",
                    })}
                  </Typography>
                  <ToggleButtonGroup
                    exclusive
                    size="small"
                    value={presentationSourceType}
                    onChange={(_e, v) => {
                      if (!v) return;
                      setPresentationSourceType(v);
                      setError("");
                    }}
                    fullWidth
                    sx={{ flexWrap: "wrap" }}
                  >
                    {PRESENTATION_SOURCE_TYPES.filter((id) => id !== "website").map((id) => (
                      <ToggleButton key={id} value={id} sx={{ flex: "1 1 auto", textTransform: "none" }}>
                        {t(`workspaceCreate.sourceType.${id}.label`, {
                          defaultValue: id === "social" ? "Rede social" : "Brief manual",
                        })}
                      </ToggleButton>
                    ))}
                  </ToggleButtonGroup>
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.75 }}>
                    {t(`workspaceCreate.sourceType.${presentationSourceType}.help`, {
                      defaultValue:
                        presentationSourceType === "social"
                          ? "Perfil público; tentamos achar o site pelo link da bio."
                          : "Nome e descrição; tentamos achar o site na busca.",
                    })}
                  </Typography>

                  {presentationSourceType === "social" ? (
                    <TextField
                      label={t("workspaceCreate.fields.socialUrl", { defaultValue: "Perfil em rede social" })}
                      helperText={t("workspaceCreate.fields.socialUrlHelp", {
                        defaultValue:
                          "Link público do perfil (ex.: instagram.com/nome-da-empresa). IDs numéricos não servem como identificador.",
                      })}
                      value={socialUrl}
                      onChange={(e) => {
                        const value = e.target.value;
                        setSocialUrl(value);
                        setError("");
                        if (!slugCustomized) {
                          const fromSocial = deriveSlugFromSocialProfileUrl(value);
                          if (fromSocial) setSlug(fromSocial);
                        }
                      }}
                      placeholder="https://www.instagram.com/suaempresa"
                      size="small"
                      autoFocus
                      fullWidth
                    />
                  ) : null}

                  {presentationSourceType === "brief" ? (
                    <Stack spacing={1.5}>
                      <TextField
                        label={t("workspaceCreate.fields.companyName", { defaultValue: "Nome da empresa" })}
                        value={companyName}
                        onChange={(e) => {
                          const value = e.target.value;
                          setCompanyName(value);
                          if (!slugCustomized) {
                            const auto = deriveSlugFromBrief({
                              companyName: value,
                              tradeName,
                            });
                            if (auto) setSlug(auto);
                          }
                        }}
                        size="small"
                        autoFocus
                        fullWidth
                      />
                      <TextField
                        label={t("workspaceCreate.fields.tradeName", {
                          defaultValue: "Nome fantasia (opcional)",
                        })}
                        value={tradeName}
                        onChange={(e) => setTradeName(e.target.value)}
                        size="small"
                        fullWidth
                      />
                      <TextField
                        label={t("workspaceCreate.fields.segment", { defaultValue: "Segmento / ramo (opcional)" })}
                        value={segment}
                        onChange={(e) => setSegment(e.target.value)}
                        placeholder={t("workspaceCreate.fields.segmentPlaceholder", {
                          defaultValue: "ex.: Turismo, Clínica veterinária",
                        })}
                        size="small"
                        fullWidth
                      />
                      <TextField
                        label={t("workspaceCreate.fields.briefDescription", {
                          defaultValue: "O que a empresa faz",
                        })}
                        helperText={t("workspaceCreate.fields.briefDescriptionHelp", {
                          defaultValue:
                            "Produtos, serviços, público-alvo e diferenciais (mín. 20 caracteres se não informar o nome).",
                        })}
                        value={briefDescription}
                        onChange={(e) => setBriefDescription(e.target.value)}
                        multiline
                        minRows={3}
                        size="small"
                        fullWidth
                      />
                      <TextField
                        label={t("workspaceCreate.fields.briefCity", { defaultValue: "Cidade / região (opcional)" })}
                        value={briefCity}
                        onChange={(e) => setBriefCity(e.target.value)}
                        size="small"
                        fullWidth
                      />
                    </Stack>
                  ) : null}
                </Stack>
              </Paper>
            ) : null}

            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
              <Stack spacing={1}>
                <Typography variant="subtitle2" fontWeight={700}>
                  {t("workspaceCreate.sections.slugTitle", { defaultValue: "Identificador do workspace" })}
                </Typography>
                {slugField}
              </Stack>
            </Paper>

            <Alert severity="info" variant="outlined">
              {t("workspaceCreate.presentationDisclaimer", {
                defaultValue:
                  "Este workspace é exclusivamente para demonstração. Clientes, leads, conversas e métricas serão fictícios, gerados por IA com base nas informações que você informar.",
              })}
            </Alert>
          </>
        ) : (
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
            <Stack spacing={1}>
              <Typography variant="subtitle2" fontWeight={700}>
                {t("workspaceCreate.sections.slugTitle", { defaultValue: "Identificador do workspace" })}
              </Typography>
              {slugField}
            </Stack>
          </Paper>
        )}

        {showClassificationFields ? (
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
            <Stack spacing={1.5}>
              <Box>
                <Typography variant="subtitle2" fontWeight={700}>
                  {t("workspaceCreate.classification.title", { defaultValue: "Classificação na plataforma" })}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                  {t("workspaceCreate.classification.lead", {
                    defaultValue:
                      "Como este workspace aparece na lista: cobrança, representante, segmento e situação.",
                  })}
                </Typography>
              </Box>
              <TextField
                select
                size="small"
                fullWidth
                label={t("platformPage.billingProfileField", { defaultValue: "Perfil de cobrança" })}
                value={billingProfile}
                onChange={(e) => {
                  const v = e.target.value;
                  setBillingProfile(v);
                  if (v === "internal" || v === "development") {
                    setPlanSku("");
                    setPlanVersion(1);
                    setSubscriptionStatus("");
                  }
                }}
                helperText={t("workspaceCreate.classification.billingHelp", {
                  defaultValue:
                    "Comercial usa plano. Uso interno e desenvolvimento não geram cobrança — não é um tipo à parte no passo anterior.",
                })}
              >
                <MenuItem value="commercial">{billingProfileLabel("commercial")}</MenuItem>
                <MenuItem value="internal">{billingProfileLabel("internal")}</MenuItem>
                <MenuItem value="development">{billingProfileLabel("development")}</MenuItem>
              </TextField>
              {isNonCommercialBilling ? (
                <Alert severity="info" variant="outlined">
                  {t("workspaceCreate.classification.nonCommercialHint", {
                    defaultValue:
                      "Sem plano e sem assinatura comercial. Representante e segmento continuam valendo para a lista.",
                  })}
                </Alert>
              ) : null}
              <TextField
                select
                size="small"
                fullWidth
                label={t("platformPage.assignReseller", { defaultValue: "Representante (opcional)" })}
                value={resellerId}
                onChange={(e) => setResellerId(e.target.value)}
                SelectProps={{ displayEmpty: true }}
              >
                <MenuItem value="">
                  {t("platformPage.assignResellerNone", { defaultValue: "Nenhuma (direto Xvision)" })}
                </MenuItem>
                {resellerOptions.map((r) => (
                  <MenuItem key={r.id} value={String(r.id)}>
                    {r.name || r.code}
                    {r.code ? ` (${r.code})` : ""}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                size="small"
                fullWidth
                label={t("platformPage.filterSegment", { defaultValue: "Segmento" })}
                value={workspaceSegment}
                onChange={(e) => setWorkspaceSegment(e.target.value)}
                SelectProps={{ displayEmpty: true }}
              >
                <MenuItem value="">
                  {t("workspaceCreate.classification.segmentNone", {
                    defaultValue: "Não definir agora",
                  })}
                </MenuItem>
                {segmentOptions.map((row) => (
                  <MenuItem key={row.segmentKey} value={row.segmentKey}>
                    {row.displayName ||
                      t(`platformPage.segmentOption.${row.segmentKey}`, {
                        defaultValue: row.segmentKey,
                      })}
                  </MenuItem>
                ))}
              </TextField>
              {!isNonCommercialBilling && (mode === "trial" || mode === "pay") ? (
                <TextField
                  select
                  size="small"
                  fullWidth
                  label={t("platformPage.filterSubscriptionStatus", {
                    defaultValue: "Situação da assinatura",
                  })}
                  value={subscriptionStatus}
                  onChange={(e) => setSubscriptionStatus(e.target.value)}
                  helperText={t("workspaceCreate.classification.statusHelp", {
                    defaultValue:
                      "Opcional. Vazio: trial fica em período de testes; pagamento segue o plano.",
                  })}
                  SelectProps={{ displayEmpty: true }}
                >
                  <MenuItem value="">
                    {t("workspaceCreate.classification.statusDefault", {
                      defaultValue: "Padrão do tipo escolhido",
                    })}
                  </MenuItem>
                  {CREATE_SUBSCRIPTION_STATUSES.map((st) => (
                    <MenuItem key={st} value={st}>
                      {subscriptionStatusLabel(st)}
                    </MenuItem>
                  ))}
                </TextField>
              ) : null}
            </Stack>
          </Paper>
        ) : null}

        {showTemplates ? (
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
            <Stack spacing={1.5}>
              <Box>
                <Typography variant="subtitle2" fontWeight={700}>
                  {t("workspaceCreate.templates.title", { defaultValue: "Como começar" })}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                  {t("workspaceCreate.templates.lead", {
                    defaultValue:
                      "Um template pronto já vem parametrizado — sem wizard no primeiro acesso. Sem template, o cliente define o básico em três passos.",
                  })}
                </Typography>
              </Box>
              <Grid container spacing={1.5}>
                <Grid item xs={12} sm={templateOptions.length ? 6 : 12}>
                  <Card
                    variant="outlined"
                    sx={{
                      height: "100%",
                      borderColor: selectedTemplateId === "blank" ? "primary.main" : "divider",
                      borderWidth: selectedTemplateId === "blank" ? 2 : 1,
                    }}
                  >
                    <CardActionArea onClick={() => setSelectedTemplateId("blank")} sx={{ p: 1.5, height: "100%" }}>
                      <Typography variant="subtitle2" fontWeight={700}>
                        {t("workspaceCreate.templates.blankTitle", { defaultValue: "Começar sem template" })}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        {t("workspaceCreate.templates.blankLead", {
                          defaultValue: "Workspace limpo. No primeiro acesso, um wizard curto pede o essencial.",
                        })}
                      </Typography>
                    </CardActionArea>
                  </Card>
                </Grid>
                {templateOptions.map((tpl) => {
                  const id = String(tpl.id);
                  const selected = selectedTemplateId === id;
                  return (
                    <Grid item xs={12} sm={6} key={id}>
                      <Card
                        variant="outlined"
                        sx={{
                          height: "100%",
                          borderColor: selected ? "primary.main" : "divider",
                          borderWidth: selected ? 2 : 1,
                        }}
                      >
                        <CardActionArea onClick={() => setSelectedTemplateId(id)} sx={{ p: 1.5, height: "100%" }}>
                          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                            <Typography variant="subtitle2" fontWeight={700}>
                              {tpl.name}
                            </Typography>
                            <Chip
                              size="small"
                              color="success"
                              variant="outlined"
                              label={t("workspaceCreate.templates.readyBadge", {
                                defaultValue: "Pronto — sem wizard",
                              })}
                            />
                          </Stack>
                          {tpl.description ? (
                            <Typography variant="body2" color="text.secondary">
                              {tpl.description}
                            </Typography>
                          ) : null}
                        </CardActionArea>
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>
              {!templateOptions.length ? (
                <Typography variant="caption" color="text.secondary">
                  {t("workspaceCreate.templates.noneForSegment", {
                    defaultValue:
                      "Nenhum template publicado neste segmento. Você pode criar um na aba Segmentos ou começar sem template.",
                  })}
                </Typography>
              ) : null}
            </Stack>
          </Paper>
        ) : null}

        {showPlanField ? (
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
            <Stack spacing={1}>
              <Typography variant="subtitle2" fontWeight={700}>
                {t("workspaceCreate.sections.planTitle", { defaultValue: "Plano" })}
              </Typography>
              <TextField
                select
                label={t("workspaceCreate.fields.plan", { defaultValue: "Plano do catálogo" })}
                value={planSelectValue}
                onChange={(e) => {
                  const v = String(e.target.value || "");
                  if (!v) {
                    setPlanSku("");
                    setPlanVersion(1);
                    return;
                  }
                  const sep = v.lastIndexOf("::");
                  const sku = sep >= 0 ? v.slice(0, sep) : v;
                  const ver = sep >= 0 ? Number(v.slice(sep + 2)) : 1;
                  setPlanSku(sku);
                  setPlanVersion(Number.isFinite(ver) && ver > 0 ? ver : 1);
                }}
                size="small"
                fullWidth
                helperText={
                  mode === "pay"
                    ? t("workspaceCreate.fields.planRequiredForPay", {
                        defaultValue: "Obrigatório no perfil comercial. Define a cobrança deste workspace.",
                      })
                    : t("workspaceCreate.fields.planOptional", {
                        defaultValue: "Opcional no trial. Se vazio, usamos o plano padrão de avaliação.",
                      })
                }
              >
                <MenuItem value="">
                  <em>{t("workspaceCreate.fields.planNone", { defaultValue: "Plano padrão" })}</em>
                </MenuItem>
                {planOptions.map((p) => (
                  <MenuItem key={p.value} value={p.value}>
                    {p.label}
                  </MenuItem>
                ))}
              </TextField>
            </Stack>
          </Paper>
        ) : null}
      </Stack>
    );
  };

  const renderProgressStep = () => {
    const pipeline = PRESENTATION_PIPELINE_STEPS(t);
    const currentStatus = presentationStatus?.status || "queued";
    const rawErrorMsg =
      presentationStatus?.errorMessage ||
      presentationStatus?.error_message ||
      "";
    const errorDisplay = presentationUserFacingError(rawErrorMsg, t);
    const errorWasSanitized =
      Boolean(String(rawErrorMsg).trim()) &&
      String(errorDisplay).trim() !== String(rawErrorMsg).trim();
    const failed = String(currentStatus || "").toLowerCase() === "failed";
    const safeIndex = presentationStatusStepIndex(currentStatus);
    const dominioId = createdWorkspaceDominioId(createdResult);
    const updatedAt = presentationStatus?.updatedAt;
    const showStaleQueuedFallback =
      staleQueuedHint && !failed && !pollError;

    return (
      <Stack spacing={2}>
        {!failed ? (
          <Typography variant="body2" color="text.secondary">
            {t("workspaceCreate.progress.intro", {
              defaultValue:
                "Estamos coletando informações do site, gerando dados de demonstração e aplicando no workspace. Isso costuma levar de 1 a 3 minutos.",
            })}
          </Typography>
        ) : null}

        {failed ? (
          <Alert severity="error">
            {t("workspaceCreate.progress.failed", {
              defaultValue: "Não foi possível concluir a demonstração automática.",
            })}
            {errorDisplay ? (
              errorWasSanitized ? (
                <Typography variant="body2" sx={{ mt: 1 }}>
                  {errorDisplay}
                </Typography>
              ) : (
                <Box sx={{ mt: 1, fontFamily: "monospace", fontSize: 12, whiteSpace: "pre-wrap" }}>
                  {errorDisplay}
                </Box>
              )
            ) : (
              <Typography variant="body2" sx={{ mt: 1 }}>
                {t("workspaceCreate.progress.failedNoDetail", {
                  defaultValue:
                    "O sistema não informou o motivo exato. Você pode descartar este workspace e criar outro, ou abrir os detalhes para tentar de novo. Se precisar de ajuda, fale com o suporte e informe o número do workspace.",
                })}
              </Typography>
            )}
          </Alert>
        ) : null}

        {pollError ? (
          <Alert
            severity={pollFailureCount >= 1 ? "error" : "warning"}
            onClose={() => setPollError("")}
          >
            {pollError}
            {pollFailureCount >= 2
              ? ` (${t("workspaceCreate.progress.pollFailures", { count: pollFailureCount, defaultValue: "{{count}} tentativas falhas" })})`
              : null}
          </Alert>
        ) : null}

        {showStaleQueuedFallback ? (
          <Alert severity="warning">
            {t("workspaceCreate.progress.staleQueued", {
              defaultValue:
                "A preparação está na fila há mais tempo que o habitual — às vezes o serviço está mais lento. Toque em Atualizar status ou aguarde alguns minutos. Se não mudar, abra os detalhes deste workspace ou fale com o suporte informando o número acima.",
            })}
          </Alert>
        ) : null}

        {!failed ? (
          <Box>
            {!presentationStatus || currentStatus !== "ready" ? (
              <LinearProgress
                sx={(theme) => ({
                  height: 8,
                  borderRadius: 1,
                  mb: 2.5,
                  bgcolor: alpha(theme.palette.primary.main, 0.12),
                  "& .MuiLinearProgress-bar": { borderRadius: 1 },
                })}
              />
            ) : null}
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
              {t("workspaceCreate.progress.pipelineCaption", {
                defaultValue: "Etapas da preparação (atualizamos automaticamente a cada poucos segundos):",
              })}
            </Typography>
            <Stepper activeStep={safeIndex} alternativeLabel sx={{ py: 0.5 }}>
              {pipeline.map((s) => (
                <Step key={s.key}>
                  <StepLabel
                    StepIconProps={{
                      sx: { "&.Mui-active": { fontWeight: 700 }, "&.Mui-completed": { color: "success.main" } },
                    }}
                  >
                    {s.label}
                  </StepLabel>
                </Step>
              ))}
            </Stepper>
          </Box>
        ) : null}

        <Stack spacing={0.75}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <Button
              type="button"
              size="small"
              variant="outlined"
              disabled={manualRefreshBusy || !dominioId}
              onClick={refreshPresentationStatus}
            >
              {manualRefreshBusy
                ? t("workspaceCreate.progress.refreshing", { defaultValue: "Atualizando…" })
                : t("workspaceCreate.progress.refreshStatus", { defaultValue: "Atualizar status" })}
            </Button>
          </Stack>
          <Typography variant="caption" color="text.secondary" sx={{ maxWidth: 560 }}>
            {t("workspaceCreate.progress.refreshManualHint", {
              defaultValue: "Use só se a etapa parecer travada por vários minutos. O fluxo segue sozinho na maioria dos casos.",
            })}
          </Typography>
        </Stack>

        {dominioId || updatedAt ? (
          <Typography variant="caption" color="text.secondary" component="div" sx={{ lineHeight: 1.5 }}>
            {dominioId
              ? t("workspaceCreate.progress.dominioId", { id: dominioId, defaultValue: "ID do workspace: {{id}}" })
              : null}
            {dominioId && updatedAt ? " · " : null}
            {updatedAt
              ? t("workspaceCreate.progress.lastServerUpdate", {
                  defaultValue: "Última atualização no servidor: {{time}}",
                  time: String(updatedAt),
                })
              : null}
          </Typography>
        ) : null}

        {!failed ? (
          <Alert severity="info" variant="outlined">
            {t("workspaceCreate.progress.runningInBackground", {
              defaultValue:
                "Você pode fechar esta janela — a preparação continua em segundo plano. Quando estiver pronta, o status será atualizado aqui ou na lista de workspaces.",
            })}
          </Alert>
        ) : null}
      </Stack>
    );
  };

  const renderDoneStep = () => {
    const dominioId = createdWorkspaceDominioId(createdResult);
    const createdForLink =
      createdResult && typeof createdResult === "object"
        ? { ...createdResult, dominioId: dominioId ?? createdResult.dominioId }
        : createdResult;
    let openUrl = null;
    if (typeof openWorkspaceUrl === "function") openUrl = openWorkspaceUrl(createdForLink);
    else if (typeof openWorkspaceUrl === "string") openUrl = openWorkspaceUrl;

    return (
      <Stack spacing={2} alignItems="center" sx={{ py: 2 }}>
        <CheckCircleIcon color="success" sx={{ fontSize: 64 }} />
        <Typography variant="h6">
          {t("workspaceCreate.done.title", { defaultValue: "Workspace criado!" })}
        </Typography>
        <Typography variant="body2" color="text.secondary" textAlign="center">
          {mode === "presentation"
            ? t("workspaceCreate.done.subtitlePresentation", {
                defaultValue: "O workspace de apresentação está pronto para ser exibido.",
              })
            : t("workspaceCreate.done.subtitleProduction", {
                defaultValue: "Tudo certo! Você já pode acessar o workspace.",
              })}
        </Typography>
        {dominioId ? (
          <Typography variant="caption" color="text.secondary">
            {t("workspaceCreate.done.dominioId", { defaultValue: "ID #{{id}}", id: dominioId })}
          </Typography>
        ) : null}
        {openUrl ? (
          <Button variant="contained" href={openUrl}>
            {t("workspaceCreate.done.open", { defaultValue: "Abrir workspace" })}
          </Button>
        ) : null}
      </Stack>
    );
  };

  return (
    <Box sx={{ minWidth: 0 }}>
      {onPresentationProgress ? (
        <Box
          sx={(theme) => ({
            mb: 3,
            pb: 2.5,
            borderBottom: `1px solid ${theme.palette.divider}`,
          })}
        >
          <Typography variant="overline" color="primary" sx={{ fontWeight: 700, letterSpacing: 0.8 }}>
            {t("workspaceCreate.progress.phaseKicker", { defaultValue: "Demonstração automática" })}
          </Typography>
          <Typography variant="h6" component="p" sx={{ mt: 0.5, fontWeight: 700 }}>
            {t("workspaceCreate.progress.phaseTitle", {
              defaultValue: "Estamos montando o workspace de apresentação",
            })}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1, maxWidth: 720, lineHeight: 1.55 }}>
            {t("workspaceCreate.progress.phaseSub", {
              defaultValue:
                "A barra e as etapas abaixo mostram o andamento. Não é necessário atualizar a página — o status muda sozinho.",
            })}
          </Typography>
        </Box>
      ) : (
        <Stepper
          activeStep={activeStep}
          sx={(theme) => ({
            mb: 3,
            "& .MuiStepConnector-line": { borderTopWidth: 3, borderColor: alpha(theme.palette.text.primary, 0.12) },
            "& .MuiStepLabel-label": { typography: { xs: "caption", sm: "body2" }, fontWeight: 500 },
            "& .MuiStepLabel-label.Mui-active": { fontWeight: 700 },
          })}
        >
          {stepsLabels.map((label, idx) => (
            <Step key={`${label}-${idx}`}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
      )}

      {stepLead && !onPresentationProgress ? (
        <Typography variant="body2" color="text.secondary" sx={{ mt: -1.5, mb: 2.5, maxWidth: 640, lineHeight: 1.55 }}>
          {stepLead}
        </Typography>
      ) : null}

      {submitting ? (
        <LinearProgress
          sx={(theme) => ({
            height: 4,
            borderRadius: 1,
            mb: 2,
            bgcolor: alpha(theme.palette.primary.main, 0.12),
          })}
        />
      ) : null}

      {error ? (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
          {error}
        </Alert>
      ) : null}

      {activeStep === 0 ? renderModeStep() : null}
      {activeStep === 1 ? renderDetailsStep() : null}
      {presentationProgressStep != null && activeStep === presentationProgressStep
        ? renderProgressStep()
        : null}
      {activeStep === finalStep ? renderDoneStep() : null}

      {showWizardFooter ? (
        <Stack direction="row" spacing={1} justifyContent="space-between" alignItems="center" sx={{ mt: 4, pt: 2, borderTop: 1, borderColor: "divider" }}>
          {showBackInFooter ? (
            <Button variant="outlined" onClick={handleBack} disabled={submitting}>
              {t("workspaceCreate.actions.back", { defaultValue: "Voltar" })}
            </Button>
          ) : (
            <span />
          )}
          {activeStep < 2 ? (
            <Button
              variant="contained"
              onClick={handleNext}
              disabled={submitting || (activeStep === 1 && slugSubmitBlocked)}
              sx={
                submitting
                  ? (theme) => ({
                      "&.Mui-disabled": {
                        bgcolor: alpha(theme.palette.primary.main, 0.85),
                        color: theme.palette.primary.contrastText,
                      },
                    })
                  : undefined
              }
            >
              {submitting ? (
                <Stack direction="row" alignItems="center" spacing={1.25} component="span">
                  <CircularProgress size={18} color="inherit" thickness={5} />
                  <span>{t("workspaceCreate.actions.submitting", { defaultValue: "Criando…" })}</span>
                </Stack>
              ) : activeStep === 1 ? (
                t("workspaceCreate.actions.create", { defaultValue: "Criar workspace" })
              ) : (
                t("workspaceCreate.actions.next", { defaultValue: "Avançar" })
              )}
            </Button>
          ) : null}
        </Stack>
      ) : null}
    </Box>
  );
};

export default WorkspaceCreateWizard;
